import { db } from '@backend/db/db'
import { functions } from '@backend/functions/functions'
import { ArrowBackIcon, RepeatIcon } from '@chakra-ui/icons'
import { Badge, Box, Button, Center, Flex, Spinner, Text, VStack } from '@chakra-ui/react'
import { usePopUpMessage } from '@hb/react'
import {
  AbilityClaimStatus,
  ALL_CLAIM_STATUSES,
  CLAIM_STATUS_LABELS,
  CLAIMS_REPORT_COLUMN_LABELS,
  CLAIMS_REPORT_COLUMNS,
  ClaimsReport,
  ClaimsReportColumn,
  DEFAULT_CLAIMS_REPORT_COLUMNS,
  DownloadClaimsReportArgs,
  GenerateClaimsReportArgs,
  MIDWIVES,
  PRACTICE_CLAIMS_REPORTS,
  WithId,
} from '@hb/shared'
import { collection, CollectionReference, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import React, { useCallback, useMemo, useState } from 'react'
import { useQuery } from '../../../hooks/backend/useQuery'
import { ActionButton, SolidActionButton } from '../../Buttons'
import { ColumnSelector } from './ColumnSelector'
import { ToggleSelectGroup } from './ToggleSelectGroup'

const generateClaimsReportFn = httpsCallable<GenerateClaimsReportArgs, { reportId: string }>(
  functions,
  'generateClaimsReport',
)

const downloadClaimsReportFn = httpsCallable<DownloadClaimsReportArgs, { signedUrl: string }>(
  functions,
  'downloadClaimsReport',
)

const ClaimsReportRow = ({ report }: { report: WithId<ClaimsReport> }) => {
  const [downloading, setDownloading] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const { showError, showMessage } = usePopUpMessage()

  const handleDownload = useCallback(async () => {
    setDownloading(true)
    try {
      const result = await downloadClaimsReportFn({
        reportId: report.id,
        practiceId: report.practiceId,
      })
      window.open(result.data.signedUrl, '_blank')
    } catch (e: any) {
      showError(e?.message ?? 'Error downloading report')
    } finally {
      setDownloading(false)
    }
  }, [report.id, report.practiceId, showError])

  const handleRegenerate = useCallback(async () => {
    setRegenerating(true)
    try {
      await generateClaimsReportFn({
        practiceId: report.practiceId,
        statuses: report.statuses,
        columns: report.columns,
      })
      showMessage({ type: 'success', text: 'Report is being generated' })
    } catch (e: any) {
      showError(e?.message ?? 'Error generating report')
    } finally {
      setRegenerating(false)
    }
  }, [report.practiceId, report.statuses, report.columns, showError, showMessage])

  const date = useMemo(() => new Date(report.createdOn).toLocaleString(), [report.createdOn])
  const columnLabels = useMemo(
    () => report.columns.map(c => CLAIMS_REPORT_COLUMN_LABELS[c]).join(', '),
    [report.columns],
  )

  return (
    <Box w="100%" borderWidth={1} borderRadius="md" p={3} bg="white" _hover={{ bg: 'gray.50' }}>
      <Flex justify="space-between" align="flex-start" gap={3}>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            {date}
          </Text>
          {report.statuses.length > 0 && (
            <Flex wrap="wrap" gap={1} mb={2}>
              {report.statuses.map(s => (
                <Badge key={s} colorScheme="blue" fontSize="xs">
                  {CLAIM_STATUS_LABELS[s]}
                </Badge>
              ))}
            </Flex>
          )}
          {report.statuses.length === 0 && (
            <Text fontSize="xs" color="gray.500" mb={2}>
              All statuses
            </Text>
          )}
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {columnLabels}
          </Text>
        </Box>
        <Flex flexShrink={0} align="center" gap={2}>
          {report.status === 'generating' && (
            <Flex align="center" gap={2}>
              <Spinner size="sm" color="blue.400" />
              <Text fontSize="sm" color="blue.500">
                Generating…
              </Text>
            </Flex>
          )}
          {report.status === 'error_generating' && (
            <Text fontSize="sm" color="red.500">
              Error
            </Text>
          )}
          {report.status === 'generated' && (
            <Button size="sm" colorScheme="green" isLoading={downloading} onClick={handleDownload}>
              Download
            </Button>
          )}
          {report.status !== 'generating' && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<RepeatIcon />}
              isLoading={regenerating}
              onClick={handleRegenerate}>
              Regenerate
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}

const ListView = ({ practiceId, onCreateNew }: { practiceId: string; onCreateNew: () => void }) => {
  const reportsRef = useMemo(
    () =>
      query(
        collection(
          db,
          MIDWIVES,
          practiceId,
          PRACTICE_CLAIMS_REPORTS,
        ) as CollectionReference<ClaimsReport>,
        orderBy('createdOn', 'desc'),
      ),
    [practiceId],
  )

  const { dataArr: reports, loading } = useQuery<ClaimsReport>(reportsRef)

  return (
    <Flex direction="column" h="100%">
      <Flex
        bg="gray.50"
        _dark={{ bg: 'gray.800' }}
        align="center"
        gap={2}
        px={4}
        py={3}
        borderBottomWidth={1}
        w="100%">
        <Text fontWeight="600" color="gray.700" _dark={{ color: 'gray.200' }} flex={1} minW={0}>
          Claims Reports
        </Text>
        <SolidActionButton size="sm" onClick={onCreateNew}>
          Create New Report
        </SolidActionButton>
      </Flex>
      <Box flex={1} overflowY="auto" p={4}>
        {loading && (
          <Center py={8}>
            <Spinner color="blue.400" />
          </Center>
        )}
        {!loading && (!reports || reports.length === 0) && (
          <Center py={8}>
            <Text color="gray.400">No reports yet. Create one to get started.</Text>
          </Center>
        )}
        {reports && reports.length > 0 && (
          <VStack spacing={2} align="stretch">
            {reports.map(report => (
              <ClaimsReportRow key={report.id} report={report} />
            ))}
          </VStack>
        )}
      </Box>
    </Flex>
  )
}

const CreateView = ({ practiceId, onBack }: { practiceId: string; onBack: () => void }) => {
  const [selectedStatuses, setSelectedStatuses] = useState<AbilityClaimStatus[]>([
    ...ALL_CLAIM_STATUSES,
  ])
  const [selectedColumns, setSelectedColumns] = useState<ClaimsReportColumn[]>([
    ...DEFAULT_CLAIMS_REPORT_COLUMNS,
  ])
  const [submitting, setSubmitting] = useState(false)
  const { showError, showMessage } = usePopUpMessage()

  const toggleStatus = useCallback((status: AbilityClaimStatus) => {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status],
    )
  }, [])

  const allStatusesSelected = selectedStatuses.length === ALL_CLAIM_STATUSES.length

  const toggleAllStatuses = useCallback(() => {
    setSelectedStatuses(allStatusesSelected ? [] : [...ALL_CLAIM_STATUSES])
  }, [allStatusesSelected])

  const handleSubmit = useCallback(async () => {
    if (!selectedColumns.length) {
      showError('Select at least one column')
      return
    }
    setSubmitting(true)
    try {
      await generateClaimsReportFn({
        practiceId,
        statuses: allStatusesSelected ? [] : selectedStatuses,
        columns: selectedColumns,
      })
      showMessage({ type: 'success', text: 'Report is being generated' })
      onBack()
    } catch (e: any) {
      showError(e?.message ?? 'Error generating report')
    } finally {
      setSubmitting(false)
    }
  }, [
    practiceId,
    selectedStatuses,
    allStatusesSelected,
    selectedColumns,
    onBack,
    showError,
    showMessage,
  ])

  return (
    <Flex direction="column" h="100%">
      <Flex
        w="100%"
        gap={3}
        align="center"
        px={4}
        py={3}
        borderBottomWidth={1}
        bg="gray.50"
        _dark={{ bg: 'gray.800' }}>
        <Text flex={1} minW={0} fontWeight="600" color="gray.700">
          New Claims Report
        </Text>
        <ActionButton leftIcon={<ArrowBackIcon />} size="sm" onClick={onBack}>
          Back to Reports
        </ActionButton>
      </Flex>
      <Box flex={1} overflowY="auto" p={4}>
        <VStack spacing={5} align="stretch">
          <ToggleSelectGroup
            title="Status Filter"
            options={ALL_CLAIM_STATUSES}
            getLabel={status => CLAIM_STATUS_LABELS[status]}
            selected={selectedStatuses}
            onToggle={toggleStatus}
            onToggleAll={toggleAllStatuses}
          />

          <ColumnSelector
            title="Columns"
            options={CLAIMS_REPORT_COLUMNS}
            getLabel={col => CLAIMS_REPORT_COLUMN_LABELS[col]}
            selected={selectedColumns}
            onChange={setSelectedColumns}
          />
        </VStack>
      </Box>
      <Flex
        bg="gray.50"
        _dark={{ bg: 'gray.800' }}
        px={4}
        py={3}
        borderTopWidth={1}
        justify="flex-end">
        <SolidActionButton
          isLoading={submitting}
          isDisabled={!selectedColumns.length}
          onClick={handleSubmit}>
          Generate Report
        </SolidActionButton>
      </Flex>
    </Flex>
  )
}

export const ClaimsTabContent: React.FC<{ practiceId: string }> = ({ practiceId }) => {
  const [view, setView] = useState<'list' | 'create'>('list')
  return view === 'list' ? (
    <ListView practiceId={practiceId} onCreateNew={() => setView('create')} />
  ) : (
    <CreateView practiceId={practiceId} onBack={() => setView('list')} />
  )
}
